/**
 * Roadmap timeline — a clickable milestone list.
 *
 * Presentation only. Interaction rules:
 *  - Real <button> headers with aria-expanded / aria-controls, so a screen
 *    reader announces the step and its state.
 *  - Roving tabindex: one tab stop for the whole timeline; Up/Down/Home/End
 *    move between steps, Enter/Space expands, Escape collapses.
 *  - Reduced motion: no height/opacity animation when the user asks for none.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Check, ChevronDown, Hammer, Lightbulb, MessageCircle, NotebookPen, Pause, Play, Timer } from "lucide-react";
import { formatDuration } from "@/lib/roadmapProgress";
import { ACTION_LABEL, type ActionKind, type Milestone } from "@/features/roadmap/roadmaps";

const ACTION_ICON: Record<ActionKind, React.ComponentType<{ className?: string }>> = {
  learn: Lightbulb,
  build: Hammer,
  talk: MessageCircle,
  reflect: NotebookPen,
};

export default function RoadmapTimeline({
  milestones,
  doneIds = [],
  onToggleDone,
  timeSpent = {},
  onLogTime,
}: {
  milestones: Milestone[];
  /** Milestone ids the learner has marked done. */
  doneIds?: string[];
  onToggleDone?: (id: string, done: boolean) => void;
  /** Seconds already logged, keyed by milestone id. */
  timeSpent?: Record<string, number>;
  onLogTime?: (id: string, seconds: number) => void;
}) {
  const reduce = useReducedMotion();
  const [openId, setOpenId] = useState<string | null>(milestones[0]?.id ?? null);
  const [focusIndex, setFocusIndex] = useState(0);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);

  /* Per-milestone stopwatch. One runs at a time; starting another banks the
     first. Only elapsed seconds are recorded — no wall-clock is stored. */
  const [timing, setTiming] = useState<{ id: string; startedAt: number } | null>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!timing) return;
    const tick = () => setElapsed(Math.floor((Date.now() - timing.startedAt) / 1000));
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [timing]);

  const bank = () => {
    if (timing && onLogTime) {
      onLogTime(timing.id, (Date.now() - timing.startedAt) / 1000);
    }
    setTiming(null);
    setElapsed(0);
  };

  const toggleTimer = (id: string) => {
    if (timing?.id === id) {
      bank();
      return;
    }
    bank();
    setTiming({ id, startedAt: Date.now() });
  };

  const move = (next: number) => {
    const i = Math.max(0, Math.min(milestones.length - 1, next));
    setFocusIndex(i);
    buttons.current[i]?.focus();
  };


  const onKeyDown = (e: React.KeyboardEvent, index: number, id: string) => {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight":
        e.preventDefault();
        move(index + 1);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        e.preventDefault();
        move(index - 1);
        break;
      case "Home":
        e.preventDefault();
        move(0);
        break;
      case "End":
        e.preventDefault();
        move(milestones.length - 1);
        break;
      case "Escape":
        if (openId === id) {
          e.preventDefault();
          setOpenId(null);
        }
        break;
      default:
        break;
    }
  };

  return (
    <ol className="relative space-y-3" aria-label="Roadmap steps">
      {milestones.map((m, i) => {
        const open = openId === m.id;
        const panelId = `${m.id}-actions`;
        const done = doneIds.includes(m.id);
        return (
          <li key={m.id} className="relative">
            {/* connector */}
            {i < milestones.length - 1 && (
              <span
                className={`absolute left-[15px] top-11 bottom-[-12px] w-px ${
                  done ? "bg-primary/50" : "bg-border/70"
                }`}
                aria-hidden="true"
              />
            )}
            <div className={`calm-card-quiet overflow-hidden ${done ? "border-primary/40" : ""}`}>
              <div className="flex items-start">
                <button
                  ref={(el) => { buttons.current[i] = el; }}
                  type="button"
                  tabIndex={focusIndex === i ? 0 : -1}
                  aria-expanded={open}
                  aria-controls={panelId}
                  onFocus={() => setFocusIndex(i)}
                  onKeyDown={(e) => onKeyDown(e, i, m.id)}
                  onClick={() => setOpenId(open ? null : m.id)}
                  className="flex min-h-14 flex-1 items-start gap-3 p-4 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <span
                    className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-display text-sm font-semibold ${
                      done
                        ? "bg-primary text-primary-foreground"
                        : open
                          ? "bg-primary/20 text-primary"
                          : "bg-muted text-muted-foreground"
                    }`}
                    aria-hidden="true"
                  >
                    {done ? <Check className="h-4 w-4" /> : i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                      {m.phase}
                      {done ? " · done" : ""}
                    </span>
                    <span
                      className={`mt-1 block font-display text-base font-semibold leading-snug ${
                        done ? "text-foreground/70 line-through" : "text-foreground"
                      }`}
                    >
                      {m.title}
                    </span>
                    <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                      {m.why}
                    </span>
                    {(timeSpent[m.id] ?? 0) > 0 && (
                      <span className="mt-1.5 inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Timer className="h-3 w-3" aria-hidden="true" />
                        {formatDuration(timeSpent[m.id])} spent
                      </span>
                    )}
                  </span>
                  <ChevronDown
                    className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                      open ? "rotate-180" : ""
                    } ${reduce ? "transition-none" : ""}`}
                    aria-hidden="true"
                  />
                </button>

                {onLogTime && (
                  <button
                    type="button"
                    onClick={() => toggleTimer(m.id)}
                    aria-pressed={timing?.id === m.id}
                    aria-label={
                      timing?.id === m.id
                        ? `Stop the timer for "${m.title}"`
                        : `Start a timer for "${m.title}"`
                    }
                    className={`my-2 ml-2 flex h-11 min-w-11 shrink-0 items-center justify-center gap-1 rounded-2xl border px-2 text-xs tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                      timing?.id === m.id
                        ? "border-primary/50 bg-primary/15 text-primary"
                        : "border-border/60 text-muted-foreground hover:border-primary/40 hover:bg-primary/8"
                    }`}
                  >
                    {timing?.id === m.id ? (
                      <>
                        <Pause className="h-4 w-4" aria-hidden="true" />
                        {formatDuration(elapsed)}
                      </>
                    ) : (
                      <Play className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                )}

                {onToggleDone && (
                  <button
                    type="button"
                    onClick={() => onToggleDone(m.id, !done)}
                    aria-pressed={done}
                    aria-label={done ? `Mark "${m.title}" as not done` : `Mark "${m.title}" as done`}
                    className={`m-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                      done
                        ? "border-primary/50 bg-primary/15 text-primary"
                        : "border-border/60 text-muted-foreground hover:border-primary/40 hover:bg-primary/8"
                    }`}
                  >
                    <Check className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>


              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    id={panelId}
                    role="group"
                    aria-label={`${m.title} — suggested actions`}
                    initial={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
                    animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
                    exit={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
                    transition={reduce ? { duration: 0 } : { duration: 0.22, ease: "easeOut" }}
                    className="overflow-hidden border-t border-border/50"
                  >
                    <ul className="space-y-2 p-4">
                      {m.actions.map((a) => {
                        const Icon = ACTION_ICON[a.kind];
                        return (
                          <li
                            key={a.label}
                            className="flex items-start gap-3 rounded-2xl bg-[hsl(var(--surface-2))] p-3"
                          >
                            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                            <span className="min-w-0 flex-1 text-sm leading-relaxed text-foreground/90">
                              <span className="font-medium">{ACTION_LABEL[a.kind]}:</span> {a.label}
                            </span>
                            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                              {a.minutes} min
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
